/**
 * AR + 360° viewer — client-side runtime.
 *
 * Two experiences off one pipeline:
 *   1. 360° viewer (all devices): orbit-spin the print in a <model-viewer>
 *      mounted over the active gallery slide. Uses the MODELS_360 set
 *      (keyed by {format}_{frame}, ignores size — for inspection only).
 *   2. View in My Room AR:
 *        - Mobile: opens a size-selection drawer first. The customer
 *          picks a size, then we launch AR for that variant:
 *            * Android: GLB + <model-viewer ar> → Scene Viewer.
 *            * iOS:     Worker /api/ar/usdz → AR Quick Look.
 *          Picking a size in the drawer does NOT update the picker's
 *          underlying selection (AR-only preview, by design).
 *        - Desktop: opens a phone-mockup QR overlay deep-linking to
 *          /pages/ar-view with the picker's current size already
 *          selected (since desktop has no AR, no size choice needed).
 *
 * Both AR/360 experiences share the same texture-swap mechanism: load
 * the base GLB into a <model-viewer>, find the `Artwork` material by
 * name, point its baseColorTexture at the design's downscaled artwork
 * URL. Mirrored UVs and frame color baked into the base model are
 * preserved automatically — the runtime only swaps the one image.
 *
 * Button mounting (changed 2026-05): the 360° button + (on desktop)
 * the View in My Room button mount as an overlay row in the bottom-
 * left of EVERY gallery slot — not just the active one. That way the
 * customer can launch AR/360 no matter which gallery image they're
 * looking at. We mount via a MutationObserver to handle slides added
 * after initial page load (Dawn does lazy hydration for non-active
 * slides).
 *
 * Reads:
 *   - product.metafields.custom.design_id  (server-rendered on data-ar-buttons)
 *   - window.__wallArtCurrentSelection      (live picker state)
 *   - wallart:selection:changed event       (selection updates)
 *   - Registry JSONs at /assets/ar-models-{360,ar}.json
 *
 * Loaded as <script src="…/ar-viewer.js" defer>. Bails immediately if
 * the AR buttons aren't in DOM (feature flag off, or not a wall-art
 * product).
 */
(function () {
  if (window.__yukiArViewerInit) return;
  window.__yukiArViewerInit = true;

  // ── Config ──────────────────────────────────────────────────────────
  const REGISTRY_360_URL = window.YukiArAssets?.registry360Url || '/assets/ar-models-360.json';
  const REGISTRY_AR_URL  = window.YukiArAssets?.registryArUrl  || '/assets/ar-models-ar.json';
  const MODEL_VIEWER_URL = window.YukiArAssets?.modelViewerUrl || '/assets/model-viewer.min.js';

  // Sizes per format — sourced from the picker contract. Hardcoded
  // here rather than read from the picker DOM to keep the AR runtime
  // independent (it can run on /pages/ar-view where the picker doesn't
  // exist). If the picker's size list ever changes, update this map
  // AND the AR-set GLB authoring + registry JSON.
  const SIZES_PER_FORMAT = {
    art_print:           ['8x10', '12x18', '16x20', '18x24', '20x30', '24x36'],
    framed_art_print:    ['8x10', '12x18', '16x20', '18x24', '20x30', '24x36'],
    canvas_print:        ['12x18', '16x24', '20x30', '24x36', '32x48', '40x60'],
    framed_canvas_print: ['12x18', '16x24', '20x30', '24x36', '32x48', '40x60'],
  };

  // ── State ───────────────────────────────────────────────────────────
  let modelViewerLoaded = false;
  let modelViewerPromise = null;
  let registry360 = null;
  let registryAR  = null;
  let registry360Promise = null;
  let registryArPromise  = null;

  // ── Mobile / platform detection ─────────────────────────────────────
  function isIOS() {
    const ua = navigator.userAgent || '';
    return /iPhone|iPad|iPod/i.test(ua) ||
      // iPadOS 13+ reports as Mac — distinguish by touch support.
      (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  }
  function isAndroid() {
    return /Android/i.test(navigator.userAgent || '');
  }
  function isMobile() {
    return isIOS() || isAndroid();
  }

  // ── Lazy <model-viewer> loader ──────────────────────────────────────
  // Don't ship the ~250KB gzipped bundle to every PDP visitor on first
  // paint — load only when they actually click an AR/360 button.
  function ensureModelViewer() {
    if (modelViewerLoaded) return Promise.resolve();
    if (modelViewerPromise) return modelViewerPromise;
    modelViewerPromise = new Promise(function (resolve, reject) {
      const s = document.createElement('script');
      s.type = 'module';
      s.src = MODEL_VIEWER_URL;
      s.onload = function () { modelViewerLoaded = true; resolve(); };
      s.onerror = function () { reject(new Error('model-viewer failed to load')); };
      document.head.appendChild(s);
    });
    return modelViewerPromise;
  }

  // ── Lazy registry loaders ───────────────────────────────────────────
  function loadRegistry360() {
    if (registry360) return Promise.resolve(registry360);
    if (registry360Promise) return registry360Promise;
    registry360Promise = fetch(REGISTRY_360_URL)
      .then(function (r) { if (!r.ok) throw new Error('reg360 ' + r.status); return r.json(); })
      .then(function (data) { registry360 = data; return data; });
    return registry360Promise;
  }
  function loadRegistryAR() {
    if (registryAR) return Promise.resolve(registryAR);
    if (registryArPromise) return registryArPromise;
    registryArPromise = fetch(REGISTRY_AR_URL)
      .then(function (r) { if (!r.ok) throw new Error('regAR ' + r.status); return r.json(); })
      .then(function (data) { registryAR = data; return data; });
    return registryArPromise;
  }

  // ── Selection helpers ───────────────────────────────────────────────
  function getCurrentSelection() {
    // 1. URL params (AR landing page /pages/ar-view receives format/
    //    size/frame via querystring from the desktop-scanned QR).
    // 2. Picker state (PDP) — set by wall-art-picker on every commit.
    // 3. Defaults (defensive, shouldn't fire in practice).
    try {
      const qs = new URLSearchParams(window.location.search);
      const format = qs.get('format');
      const size = qs.get('size');
      const frame = qs.get('frame');
      if (format && size && frame) {
        return { format: format, size: size, frameColor: frame };
      }
    } catch (e) { /* SSR / no location — fall through */ }

    const sel = window.__wallArtCurrentSelection || {};
    return {
      format: sel.format || 'framed_art_print',
      size: sel.size || '24x36',
      frameColor: sel.frameColor || 'none',
    };
  }

  function getDesignUuid() {
    // Server-renders this as a data-attr on the AR buttons container.
    const el = document.querySelector('[data-ar-buttons]');
    return (el && el.getAttribute('data-design-uuid')) || '';
  }

  function getDesignTextureUrls() {
    // Server-renders the per-aspect URLs as data-attrs on the AR buttons
    // container (from product.metafields.custom.artwork_url_2_3 etc).
    // Older designs may have the metafield pointing at the full-res
    // (~9MB) master rather than the downscaled (~500KB) variant —
    // happens when storefront-product-creator ran BEFORE the downscaled
    // column had a value. AR doesn't need 9MB; transform the URL to
    // derive the lowres variant.
    const el = document.querySelector('[data-ar-buttons]');
    if (!el) return {};
    return {
      '2x3': preferLowresArtwork(el.getAttribute('data-artwork-2x3') || ''),
      '3x4': preferLowresArtwork(el.getAttribute('data-artwork-3x4') || ''),
      '4x5': preferLowresArtwork(el.getAttribute('data-artwork-4x5') || ''),
    };
  }

  /** Transform a full-res GCS URL to its `_lowres` variant. No-op if
   *  the URL already contains `_lowres`. If the lowres object doesn't
   *  exist on GCS for an unusually-old design, the texture load will
   *  404 (visible in DevTools Network); fix that design with a
   *  storefront-product-creator re-run. */
  function preferLowresArtwork(url) {
    if (!url) return url;
    if (/_lowres\./i.test(url)) return url;
    return url.replace(/_(\d+x\d+)\.(jpe?g|png|webp)(\?|$)/i, '_$1_lowres.$2$3');
  }

  // ── Registry lookup ─────────────────────────────────────────────────
  function lookup360Url(format, frameColor) {
    if (!registry360) return null;
    const isFramed = format === 'framed_art_print' || format === 'framed_canvas_print';
    const key = isFramed ? format + '_' + frameColor : format;
    return registry360[key] || null;
  }
  function lookupArUrl(format, size, frameColor) {
    if (!registryAR) return null;
    const isFramed = format === 'framed_art_print' || format === 'framed_canvas_print';
    const key = isFramed
      ? format + '_' + size + '_' + frameColor
      : format + '_' + size;
    return registryAR[key] || null;
  }

  // ── Texture swap on <model-viewer> ──────────────────────────────────
  // `forceWaitForLoad`: true when called after setting a NEW src on an
  // already-loaded model-viewer (the live-reload path on variant change).
  // Without it, `mv.loaded` is still true from the previous model for a
  // brief window, so we skip the wait and end up calling setTexture on
  // material references that get immediately garbage-collected when the
  // new model finishes loading — result: new model renders with its
  // 1×1 placeholder texture, appearing black/transparent on the wall.
  async function swapTextureWhenLoaded(mv, designTextureUrl, forceWaitForLoad) {
    console.log('[ar-viewer] swap requested → ' + designTextureUrl + (forceWaitForLoad ? ' (reload)' : ''));
    if (forceWaitForLoad || !mv.loaded) {
      await new Promise(function (resolve) {
        mv.addEventListener('load', resolve, { once: true });
      });
    }
    if (!mv.model || !mv.model.materials) {
      console.warn('[ar-viewer] mv.model not ready after load event');
      return;
    }
    const artwork = mv.model.materials.find(function (m) { return m.name === 'Artwork'; });
    if (!artwork) {
      console.warn('[ar-viewer] base GLB has no material named "Artwork" — skipping texture swap');
      return;
    }
    try {
      const baseColorInfo = artwork.pbrMetallicRoughness.baseColorTexture;
      if (typeof mv.createTexture === 'function') {
        const newTexture = await mv.createTexture(designTextureUrl);
        if (!newTexture) throw new Error('mv.createTexture returned null');
        baseColorInfo.setTexture(newTexture);
      } else if (mv.model && typeof mv.model.createTexture === 'function') {
        const newTexture = await mv.model.createTexture(designTextureUrl);
        if (!newTexture) throw new Error('mv.model.createTexture returned null');
        baseColorInfo.setTexture(newTexture);
      } else if (baseColorInfo.texture && baseColorInfo.texture.source && typeof baseColorInfo.texture.source.setURI === 'function') {
        await baseColorInfo.texture.source.setURI(designTextureUrl);
      } else {
        console.warn('[ar-viewer] no known texture-swap API on this model-viewer build');
      }
    } catch (err) {
      console.error('[ar-viewer] texture swap failed:', err);
    }
  }

  // ── 360° viewer overlay ─────────────────────────────────────────────
  // Composition (matches the spec mockup):
  //   ┌──────────────────────────────────────┐
  //   │ [360°]                       [+] [−] │
  //   │                                       │
  //   │           <model-viewer/>             │
  //   │                                       │
  //   │            [VIEW IN MY ROOM]    [×]   │
  //   └──────────────────────────────────────┘
  //
  //   • Gray bg, not white — distinguishes overlay from product image.
  //   • 360° badge top-left — explicit affordance: "you're in 360 mode".
  //   • Zoom controls top-right — adjust model-viewer cameraOrbit radius.
  //   • VIEW IN MY ROOM bottom-right — pivot from inspection (360) to
  //     placement (AR) without closing+re-opening. Same handler as the
  //     main AR button — opens size drawer (mobile) or QR panel (desktop).
  //   • × bottom-right — closes the 360 overlay.
  //
  // Live updates: subscribed to wallart:selection:changed. If the open
  // 360 overlay's current format/frame no longer matches the picker,
  // we reload the base GLB in-place (texture URL doesn't change since
  // the design is fixed for this PDP).
  let activeOverlay = null;
  let activeOverlayMv = null;
  let activeOverlayKey = null;   // "format_frame" — what's currently displayed

  async function open360Viewer(triggerBtn) {
    if (activeOverlay) return;

    const anchor = triggerBtn
      ? triggerBtn.closest('.product-media-container')
      : findActiveSlideContainer();
    if (!anchor) {
      console.warn('[ar-viewer] no gallery slide to overlay 360 viewer onto');
      return;
    }

    try {
      await Promise.all([ensureModelViewer(), loadRegistry360()]);
    } catch (err) {
      console.error('[ar-viewer] failed to bootstrap 360 viewer:', err);
      return;
    }

    const sel = getCurrentSelection();
    const baseGlbUrl = lookup360Url(sel.format, sel.frameColor);
    if (!baseGlbUrl) {
      alert('360° viewer not available for this configuration yet — coming soon.');
      return;
    }

    const aspect = window.YukiArAspect.chooseAspect(sel.size);
    const textures = getDesignTextureUrls();
    const designTextureUrl = textures[aspect] || textures['2x3'] || '';
    if (!designTextureUrl) {
      console.warn('[ar-viewer] no design texture URL on data attrs');
      return;
    }

    const overlay = document.createElement('div');
    overlay.className = 'yuki-ar-360-overlay';

    // model-viewer
    const mv = document.createElement('model-viewer');
    mv.setAttribute('src', baseGlbUrl);
    mv.setAttribute('camera-controls', '');
    mv.setAttribute('touch-action', 'pan-y');
    mv.setAttribute('shadow-intensity', '0.6');
    mv.setAttribute('exposure', '1.0');
    mv.setAttribute('environment-image', 'neutral');
    mv.setAttribute('auto-rotate', '');
    mv.setAttribute('auto-rotate-delay', '2000');
    overlay.appendChild(mv);

    // 360° badge top-left
    const badge = document.createElement('div');
    badge.className = 'yuki-ar-360-overlay__badge';
    badge.innerHTML =
      '<svg viewBox="0 0 28 14" width="40" height="20" aria-hidden="true">' +
        '<text x="0" y="11" font-family="-apple-system, system-ui, sans-serif" ' +
        'font-size="11" font-weight="700" fill="currentColor">360°</text>' +
      '</svg>' +
      '<svg viewBox="0 0 18 8" width="22" height="10" aria-hidden="true" style="margin-top:2px;">' +
        '<path d="M3 4 Q 9 -1 15 4" fill="none" stroke="currentColor" stroke-width="1.2" stroke-linecap="round"/>' +
        '<path d="M13 2 L 15.5 4 L 13 6" fill="none" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/>' +
      '</svg>';
    overlay.appendChild(badge);

    // Zoom controls top-right
    const zoom = document.createElement('div');
    zoom.className = 'yuki-ar-360-overlay__zoom';
    zoom.innerHTML =
      '<button type="button" class="yuki-ar-360-overlay__zoom-btn" data-zoom="in" aria-label="Zoom in">' +
        '<svg viewBox="0 0 20 20" width="18" height="18" aria-hidden="true">' +
          '<circle cx="9" cy="9" r="6" fill="none" stroke="currentColor" stroke-width="1.6"/>' +
          '<path d="M14 14 L18 18" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>' +
          '<path d="M6 9 H12 M9 6 V12" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>' +
        '</svg>' +
      '</button>' +
      '<button type="button" class="yuki-ar-360-overlay__zoom-btn" data-zoom="out" aria-label="Zoom out">' +
        '<svg viewBox="0 0 20 20" width="18" height="18" aria-hidden="true">' +
          '<circle cx="9" cy="9" r="6" fill="none" stroke="currentColor" stroke-width="1.6"/>' +
          '<path d="M14 14 L18 18" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>' +
          '<path d="M6 9 H12" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>' +
        '</svg>' +
      '</button>';
    overlay.appendChild(zoom);
    zoom.addEventListener('click', function (e) {
      const btn = e.target.closest('[data-zoom]');
      if (!btn) return;
      adjust360Zoom(mv, btn.getAttribute('data-zoom') === 'in' ? -1 : 1);
    });

    // View in My Room button bottom-right (pivot to AR from inspection).
    // Uses the same label + icon settings as the main overlay-row button.
    const cfg = getCfg();
    const arBtn = document.createElement('button');
    arBtn.type = 'button';
    arBtn.className = 'yuki-ar-360-overlay__room-btn';
    arBtn.setAttribute('data-ar-room', '');  // delegated handler picks this up
    arBtn.setAttribute('aria-label', cfg.arvr.label || 'View in my room');
    arBtn.innerHTML =
      buildIconHTML('arvr', cfg.arvr.showIcon, cfg.arvr.iconUrl) +
      '<span>' + escapeHtml((cfg.arvr.label || 'View in My Room').toUpperCase()) + '</span>';
    overlay.appendChild(arBtn);

    // Close × button bottom-right
    const close = document.createElement('button');
    close.type = 'button';
    close.className = 'yuki-ar-360-overlay__close';
    close.setAttribute('aria-label', 'Close 360° viewer');
    close.textContent = '×';
    close.addEventListener('click', close360Viewer);
    overlay.appendChild(close);

    anchor.style.position = anchor.style.position || 'relative';
    anchor.appendChild(overlay);
    activeOverlay = overlay;
    activeOverlayMv = mv;
    activeOverlayKey = sel.format + '|' + (sel.frameColor || 'none');

    try {
      await swapTextureWhenLoaded(mv, designTextureUrl);
    } catch (err) {
      console.error('[ar-viewer] texture swap failed:', err);
    }
  }

  function close360Viewer() {
    if (!activeOverlay) return;
    activeOverlay.remove();
    activeOverlay = null;
    activeOverlayMv = null;
    activeOverlayKey = null;
  }

  /** Adjust the open 360 viewer's camera distance.
   *  direction: -1 to zoom in (smaller radius), +1 to zoom out.
   *  Steps by 15% per click; model-viewer's clamps handle min/max. */
  function adjust360Zoom(mv, direction) {
    try {
      const orbit = mv.getCameraOrbit();
      const factor = direction < 0 ? 0.85 : 1.15;
      const newRadius = Math.max(0.1, orbit.radius * factor);
      mv.cameraOrbit = orbit.theta + 'rad ' + orbit.phi + 'rad ' + newRadius + 'm';
    } catch (err) {
      console.warn('[ar-viewer] zoom adjust failed:', err);
    }
  }

  /** Live-reload the 360 viewer when the picker's format/frame changes.
   *  No-op if 360 viewer isn't open or if the new variant maps to the
   *  same base model (size-only changes don't affect 360 since the
   *  registry key is {format}_{frame} — size is ignored). */
  async function maybeReload360OnSelectionChange() {
    if (!activeOverlayMv) return;
    if (!registry360) return;  // shouldn't happen if overlay is open, but defensive

    const sel = getCurrentSelection();
    const newKey = sel.format + '|' + (sel.frameColor || 'none');
    if (newKey === activeOverlayKey) return;  // same base model

    const baseGlbUrl = lookup360Url(sel.format, sel.frameColor);
    if (!baseGlbUrl) {
      // No 360 model for this variant — close the overlay rather than
      // leave a stale model up.
      console.warn('[ar-viewer] 360 reload: no model for', sel.format, sel.frameColor, '— closing overlay');
      close360Viewer();
      return;
    }

    const aspect = window.YukiArAspect.chooseAspect(sel.size);
    const textures = getDesignTextureUrls();
    const designTextureUrl = textures[aspect] || textures['2x3'] || '';

    activeOverlayMv.setAttribute('src', baseGlbUrl);
    activeOverlayKey = newKey;
    try {
      // forceWaitForLoad=true → wait for the NEW model's load event
      // before touching materials. Without this we'd grab references to
      // the old model's materials (which get garbage-collected when the
      // new one finishes loading) and the swap would render as black.
      await swapTextureWhenLoaded(activeOverlayMv, designTextureUrl, true);
    } catch (err) {
      console.error('[ar-viewer] 360 reload texture swap failed:', err);
    }
  }

  function findActiveSlideContainer() {
    return document.querySelector('media-gallery slider-component li.is-active .product-media-container')
      || document.querySelector('media-gallery slider-component li:not([hidden]) .product-media-container')
      || document.querySelector('media-gallery .product-media-container');
  }

  // ── Android AR launch ───────────────────────────────────────────────
  // Accepts a size override (from the mobile size drawer); falls back
  // to the picker's current selection if not provided.
  async function openAndroidAR(sizeOverride) {
    try {
      await Promise.all([ensureModelViewer(), loadRegistryAR()]);
    } catch (err) {
      console.error('[ar-viewer] failed to bootstrap Android AR:', err);
      return;
    }

    const sel = getCurrentSelection();
    const size = sizeOverride || sel.size;
    const baseGlbUrl = lookupArUrl(sel.format, size, sel.frameColor);
    if (!baseGlbUrl) {
      alert('AR not available for this size — try a different size.');
      return;
    }

    const aspect = window.YukiArAspect.chooseAspect(size);
    const textures = getDesignTextureUrls();
    const designTextureUrl = textures[aspect] || textures['2x3'] || '';
    if (!designTextureUrl) {
      console.warn('[ar-viewer] no design texture URL');
      return;
    }

    let mv = document.querySelector('model-viewer[data-yuki-ar-launcher]');
    if (mv) mv.remove();
    mv = document.createElement('model-viewer');
    mv.setAttribute('data-yuki-ar-launcher', '');
    mv.setAttribute('src', baseGlbUrl);
    mv.setAttribute('ar', '');
    mv.setAttribute('ar-modes', 'scene-viewer webxr');
    mv.setAttribute('ar-placement', 'wall');  // wall art hangs on a vertical surface, not the floor (default)
    mv.setAttribute('ar-scale', 'fixed');  // locks pinch-to-scale in Scene Viewer
    mv.setAttribute('exposure', '1.0');
    mv.style.cssText = 'position:fixed;left:-9999px;top:-9999px;width:1px;height:1px;';
    document.body.appendChild(mv);

    try {
      await swapTextureWhenLoaded(mv, designTextureUrl);
      requestAnimationFrame(function () {
        try {
          mv.activateAR();
        } catch (err) {
          console.error('[ar-viewer] activateAR failed:', err);
        }
      });
    } catch (err) {
      console.error('[ar-viewer] AR texture swap failed:', err);
    }
  }

  // ── View in My Room dispatcher ──────────────────────────────────────
  // Mobile: open the size drawer. The drawer's per-size tap is what
  // actually triggers AR (with the user's fresh gesture).
  // Desktop: open the phone-mockup QR overlay deep-linking to the
  // landing page on the customer's phone.
  function openViewInMyRoom(triggerBtn) {
    if (isMobile()) {
      openMobileSizeDrawer(triggerBtn);
    } else {
      openDesktopArPanel(triggerBtn);
    }
  }

  // ── iOS AR Quick Look ───────────────────────────────────────────────
  // Apple requires AR Quick Look to launch from a user gesture. If the
  // USDZ isn't cached (Cloud Run cold ~5-20s), the gesture goes stale.
  // We re-arm the SAME button on a `ready` response so the user's
  // eventual second tap launches with a fresh gesture.
  //
  // Called from the size-drawer per-row tap with sizeOverride set.
  let iosPendingPoll = null;

  async function openIosAR(triggerBtn, sizeOverride) {
    const sel = getCurrentSelection();
    const size = sizeOverride || sel.size;

    const designUuid = getDesignUuid();
    if (!designUuid) {
      alert('AR not available for this product (missing design ID).');
      return;
    }

    const url = '/apps/api/ar/usdz'
      + '?design=' + encodeURIComponent(designUuid)
      + '&format=' + encodeURIComponent(sel.format)
      + '&size=' + encodeURIComponent(size)
      + '&frame=' + encodeURIComponent(sel.frameColor);

    setPendingState(triggerBtn, 'Preparing your AR preview…');

    try {
      const res = await fetch(url, { headers: { 'Accept': 'application/json' } });
      const data = await res.json();

      if (res.ok && data.status === 'ready' && data.usdz_url) {
        clearPendingState(triggerBtn);
        launchIosArQuickLook(data.usdz_url);
        return;
      }

      if (res.ok && data.status === 'generating') {
        scheduleIosArPoll(triggerBtn, url, data.poll_after_ms || 1500);
        setPendingState(triggerBtn, 'Generating AR preview (~10s)…');
        return;
      }

      clearPendingState(triggerBtn);
      alert('AR preview unavailable: ' + (data.error || ('HTTP ' + res.status)));
    } catch (err) {
      clearPendingState(triggerBtn);
      console.error('[ar-viewer] iOS AR fetch failed:', err);
      alert('AR preview failed to load. Please try again in a moment.');
    }
  }

  function scheduleIosArPoll(triggerBtn, pollUrl, delayMs) {
    if (iosPendingPoll) clearTimeout(iosPendingPoll);
    iosPendingPoll = setTimeout(async function () {
      try {
        const res = await fetch(pollUrl, { headers: { 'Accept': 'application/json' } });
        const data = await res.json();
        if (data.status === 'ready' && data.usdz_url) {
          rearmButton(triggerBtn, data.usdz_url);
        } else if (data.status === 'generating') {
          scheduleIosArPoll(triggerBtn, pollUrl, Math.min(5000, (data.poll_after_ms || 2000) + 500));
        } else {
          clearPendingState(triggerBtn);
          alert('AR preview failed: ' + (data.error || 'unknown error'));
        }
      } catch (err) {
        console.error('[ar-viewer] iOS poll failed:', err);
        clearPendingState(triggerBtn);
      }
    }, delayMs);
  }

  function rearmButton(triggerBtn, usdzUrl) {
    if (!triggerBtn) { launchIosArQuickLook(usdzUrl); return; }
    triggerBtn.textContent = 'Tap to view in your room';
    triggerBtn.disabled = false;
    triggerBtn.style.opacity = '1';
    triggerBtn.dataset.arReady = '1';
    triggerBtn.dataset.usdzUrl = usdzUrl;
  }

  function launchIosArQuickLook(usdzUrl) {
    const a = document.createElement('a');
    a.href = usdzUrl;
    a.rel = 'ar';
    const img = document.createElement('img');
    img.alt = '';
    a.appendChild(img);
    document.body.appendChild(a);
    a.click();
    setTimeout(function () { a.remove(); }, 0);
  }

  function setPendingState(btn, label) {
    if (!btn) return;
    if (!btn.dataset.originalLabel) {
      btn.dataset.originalLabel = btn.textContent || '';
    }
    btn.textContent = label;
    btn.disabled = true;
    btn.style.opacity = '0.65';
  }

  function clearPendingState(btn) {
    if (!btn) return;
    if (btn.dataset.originalLabel) {
      btn.textContent = btn.dataset.originalLabel;
      delete btn.dataset.originalLabel;
    }
    btn.disabled = false;
    btn.style.opacity = '1';
    delete btn.dataset.arReady;
    delete btn.dataset.usdzUrl;
  }

  // ── Mobile size selection drawer ────────────────────────────────────
  // Bottom-sheet drawer listing sizes available for the picker's
  // current format. Tap a size → close drawer → launch AR for that
  // variant. The tap satisfies iOS's user-gesture requirement, so
  // launching from inside the per-row click handler is safe.
  let activeSizeDrawer = null;

  function openMobileSizeDrawer(triggerBtn) {
    if (activeSizeDrawer) return;

    const sel = getCurrentSelection();
    const sizes = SIZES_PER_FORMAT[sel.format] || SIZES_PER_FORMAT.framed_art_print;

    const backdrop = document.createElement('div');
    backdrop.className = 'yuki-ar-drawer-backdrop';

    const drawer = document.createElement('div');
    drawer.className = 'yuki-ar-drawer';

    // Header
    const header = document.createElement('div');
    header.className = 'yuki-ar-drawer__header';
    header.innerHTML =
      '<h3 class="yuki-ar-drawer__title">Choose your size</h3>' +
      '<p class="yuki-ar-drawer__subtitle">Preview at real-world scale in your room.</p>' +
      '<button type="button" class="yuki-ar-drawer__close" aria-label="Close">×</button>';
    drawer.appendChild(header);

    // Size grid
    const grid = document.createElement('div');
    grid.className = 'yuki-ar-drawer__grid';
    sizes.forEach(function (size) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'yuki-ar-drawer__size';
      btn.setAttribute('data-size', size);
      // Match the wall-art-picker's display convention (`24"x36"`).
      // Underlying state + data-size attr stay canonical (`24x36`) so
      // AR registry lookups + URL params still resolve correctly.
      const m = String(size).match(/^(\d+)x(\d+)$/);
      btn.textContent = m ? m[1] + '"x' + m[2] + '"' : String(size);
      if (size === sel.size) btn.classList.add('is-current');
      grid.appendChild(btn);
    });
    drawer.appendChild(grid);

    document.body.appendChild(backdrop);
    document.body.appendChild(drawer);
    activeSizeDrawer = { drawer: drawer, backdrop: backdrop };

    // Animate in next frame so CSS transition fires
    requestAnimationFrame(function () {
      drawer.classList.add('is-open');
      backdrop.classList.add('is-open');
    });

    function closeDrawer() {
      if (!activeSizeDrawer) return;
      drawer.classList.remove('is-open');
      backdrop.classList.remove('is-open');
      setTimeout(function () {
        backdrop.remove();
        drawer.remove();
      }, 250);
      activeSizeDrawer = null;
    }

    backdrop.addEventListener('click', closeDrawer);
    header.querySelector('.yuki-ar-drawer__close').addEventListener('click', closeDrawer);

    grid.addEventListener('click', function (e) {
      const sizeBtn = e.target.closest('.yuki-ar-drawer__size');
      if (!sizeBtn) return;
      const size = sizeBtn.getAttribute('data-size');
      closeDrawer();
      // Fire AR for the chosen size. The size tap counts as a fresh
      // user gesture, so iOS AR Quick Look launches from THIS handler
      // (or, on async USDZ-generation paths, from the eventual re-arm
      // tap on the originating button — clearPending will reset it).
      if (isAndroid()) {
        openAndroidAR(size);
      } else {
        openIosAR(triggerBtn, size);
      }
    });
  }

  // ── Desktop AR panel (phone-mockup QR overlay) ──────────────────────
  // Replaces the previous centered modal with a styled phone-frame
  // mockup that overlays the active gallery slide — closer to the
  // visual pattern competing wall-art sites use, signals "this is for
  // your phone" more obviously than a generic dialog.
  let qrLibPromise = null;
  function ensureQrLib() {
    if (window.QRCode) return Promise.resolve();
    if (qrLibPromise) return qrLibPromise;
    qrLibPromise = new Promise(function (resolve, reject) {
      const s = document.createElement('script');
      s.src = (window.YukiArAssets && window.YukiArAssets.qrcodeUrl) || '/assets/qrcode.min.js';
      s.onload = resolve;
      s.onerror = function () { reject(new Error('qrcode.min.js failed to load')); };
      document.head.appendChild(s);
    });
    return qrLibPromise;
  }

  function openDesktopArPanel(triggerBtn) {
    const sel = getCurrentSelection();
    const designUuid = getDesignUuid();
    if (!designUuid) {
      alert('AR not available for this product.');
      return;
    }

    // Anchor the panel to the slide containing the clicked button.
    const anchor = triggerBtn
      ? triggerBtn.closest('.product-media-container')
      : findActiveSlideContainer();
    if (!anchor) {
      console.warn('[ar-viewer] no gallery slide to anchor desktop AR panel onto');
      return;
    }

    const landingUrl = window.location.origin
      + '/pages/ar-view'
      + '?design=' + encodeURIComponent(designUuid)
      + '&format=' + encodeURIComponent(sel.format)
      + '&size=' + encodeURIComponent(sel.size)
      + '&frame=' + encodeURIComponent(sel.frameColor);

    ensureQrLib().then(function () {
      showPhoneMockupOverlay(anchor, landingUrl);
    }).catch(function (err) {
      console.error('[ar-viewer] QR lib failed:', err);
      prompt('Open this URL on your phone to preview in AR:', landingUrl);
    });
  }

  function showPhoneMockupOverlay(anchor, url) {
    // Close any existing.
    const existing = anchor.querySelector('.yuki-ar-phone-overlay');
    if (existing) existing.remove();

    const overlay = document.createElement('div');
    overlay.className = 'yuki-ar-phone-overlay';
    overlay.innerHTML =
      '<div class="yuki-ar-phone-card">' +
        '<div class="yuki-ar-phone-card__title">View in My Room</div>' +
        '<div class="yuki-ar-phone-card__hint">' +
          'Point your phone\'s camera at this code to launch AR for your selected size and frame.' +
        '</div>' +
        '<div class="yuki-ar-phone-card__qr"></div>' +
      '</div>' +
      '<button type="button" class="yuki-ar-phone-overlay__close" aria-label="Close">×</button>';

    anchor.style.position = anchor.style.position || 'relative';
    anchor.appendChild(overlay);

    const target = overlay.querySelector('.yuki-ar-phone-card__qr');
    new window.QRCode(target, {
      text: url,
      width: 180,
      height: 180,
      correctLevel: window.QRCode.CorrectLevel ? window.QRCode.CorrectLevel.M : 0,
    });

    overlay.querySelector('.yuki-ar-phone-overlay__close').addEventListener('click', function () {
      overlay.remove();
    });
    overlay.addEventListener('click', function (e) {
      if (e.target === overlay) overlay.remove();
    });
  }

  // ── Theme-editor config helpers ────────────────────────────────────
  // window.YukiArConfig is set by ar-buttons.liquid from section
  // settings — label text + icon visibility + optional custom icon URL.
  // Provides safe defaults if the snippet didn't run (shouldn't happen
  // when the AR buttons are mounted, but defensive).
  function getCfg() {
    return window.YukiArConfig || {
      ar360: { label: '360°', showIcon: false, iconUrl: '' },
      arvr:  { label: 'View in My Room', showIcon: true, iconUrl: '' },
    };
  }

  /** Build the icon markup for an overlay button. Returns either:
   *  - empty string if showIcon is false
   *  - an <img> tag if a custom icon URL is set
   *  - a default inline SVG for the named button kind
   */
  function buildIconHTML(kind, showIcon, iconUrl) {
    if (!showIcon) return '';
    if (iconUrl) {
      return '<img class="yuki-ar-overlay-btn__icon" src="' + iconUrl + '" alt="" aria-hidden="true">';
    }
    if (kind === 'ar360') {
      // Curved arrow — paired with "360°" label.
      return (
        '<svg class="yuki-ar-overlay-btn__icon" viewBox="0 0 24 12" aria-hidden="true">' +
          '<path d="M3 6 Q 12 -1 21 6" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>' +
          '<path d="M18 3 L 21.5 6 L 18 9" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>' +
        '</svg>'
      );
    }
    // VR / View in My Room — phone with AR cube on screen.
    return (
      '<svg class="yuki-ar-overlay-btn__icon" viewBox="0 0 24 24" aria-hidden="true">' +
        '<rect x="6" y="2" width="12" height="20" rx="2" fill="none" stroke="currentColor" stroke-width="1.6"/>' +
        '<path d="M9 8l3-1.5L15 8v3l-3 1.5L9 11z" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linejoin="round"/>' +
        '<path d="M9 8l3 1.5L15 8M12 9.5v3" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linejoin="round"/>' +
        '<circle cx="12" cy="19" r="0.8" fill="currentColor"/>' +
      '</svg>'
    );
  }

  function escapeHtml(s) {
    return String(s).replace(/[&<>"']/g, function (c) {
      return ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c];
    });
  }

  // ── Mount overlay row (360 + desktop View in My Room) per slot ─────
  // Mount in EVERY gallery slot's .product-media-container, not just
  // the active one. Customer can launch AR/360 from any slide. We use
  // a MutationObserver so dynamically-added slides (Dawn lazy hydrates
  // non-active slides on first interaction) get buttons too.
  function mountOverlayRow(container) {
    if (!container || container.querySelector('.yuki-ar-overlay-row')) return;

    const cfg = getCfg();

    const row = document.createElement('div');
    row.className = 'yuki-ar-overlay-row';

    // View in My Room button — desktop-only display via CSS.
    const roomBtn = document.createElement('button');
    roomBtn.type = 'button';
    roomBtn.className = 'yuki-ar-overlay-btn yuki-ar-overlay-btn--room';
    roomBtn.setAttribute('data-ar-room', '');
    roomBtn.setAttribute('aria-label', cfg.arvr.label || 'View in my room');
    roomBtn.innerHTML =
      buildIconHTML('arvr', cfg.arvr.showIcon, cfg.arvr.iconUrl) +
      '<span>' + escapeHtml(cfg.arvr.label || 'View in My Room') + '</span>';
    row.appendChild(roomBtn);

    // 360° button — visible on all platforms.
    const btn360 = document.createElement('button');
    btn360.type = 'button';
    btn360.className = 'yuki-ar-overlay-btn yuki-ar-overlay-btn--360';
    btn360.setAttribute('data-ar-360', '');
    btn360.setAttribute('aria-label', cfg.ar360.label || '360 degree view');
    btn360.innerHTML =
      buildIconHTML('ar360', cfg.ar360.showIcon, cfg.ar360.iconUrl) +
      '<span>' + escapeHtml(cfg.ar360.label || '360°') + '</span>';
    row.appendChild(btn360);

    if (getComputedStyle(container).position === 'static') {
      container.style.position = 'relative';
    }
    container.appendChild(row);
  }

  function mountOverlayRowsEverywhere() {
    const containers = document.querySelectorAll('media-gallery .product-media-container');
    containers.forEach(mountOverlayRow);
  }

  function setupOverlayMounting() {
    mountOverlayRowsEverywhere();

    // Catch slides added by Dawn after init (lazy hydration on first
    // gallery interaction).
    const gallery = document.querySelector('media-gallery');
    if (!gallery) return;
    const observer = new MutationObserver(function () {
      mountOverlayRowsEverywhere();
    });
    observer.observe(gallery, { childList: true, subtree: true });
  }

  // ── Wire up ─────────────────────────────────────────────────────────
  function init() {
    const buttons = document.querySelector('[data-ar-buttons]');
    if (!buttons) return;  // feature flag off, or no design_uuid

    setupOverlayMounting();

    // Event delegation handles both overlay buttons (per-slide) and
    // the below-gallery mobile button (single instance).
    document.addEventListener('click', function (e) {
      const btn360 = e.target.closest && e.target.closest('[data-ar-360]');
      if (btn360) {
        e.preventDefault();
        open360Viewer(btn360);
        return;
      }
      const btnAR = e.target.closest && e.target.closest('[data-ar-room]');
      if (btnAR) {
        e.preventDefault();
        if (btnAR.dataset.arReady === '1' && btnAR.dataset.usdzUrl) {
          // iOS re-armed path — launch with this fresh tap as gesture.
          const url = btnAR.dataset.usdzUrl;
          clearPendingState(btnAR);
          launchIosArQuickLook(url);
        } else {
          openViewInMyRoom(btnAR);
        }
        return;
      }
    });

    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape') return;
      if (activeOverlay) close360Viewer();
      if (activeSizeDrawer) activeSizeDrawer.drawer.querySelector('.yuki-ar-drawer__close')?.click();
      const phoneOverlay = document.querySelector('.yuki-ar-phone-overlay');
      if (phoneOverlay) phoneOverlay.remove();
    });

    // Live-update the 360 viewer when the picker commits a new
    // format/frame (size-only changes are ignored — 360 doesn't use it).
    // The event is fired by assets/wall-art-picker.js on every commit.
    window.addEventListener('wallart:selection:changed', function () {
      maybeReload360OnSelectionChange();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
